import type { AdminWhatsappSendLogRow } from '../../../domain/superadmin/types';
import { Button } from '../../../components/ui';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import { formatDate } from '../../../utils/formatters';
import { whatsappSendErrorText } from './whatsappSendErrorText';

type Props = {
    items: AdminWhatsappSendLogRow[];
    total: number;
    limit: number;
    offset: number;
    isLoading: boolean;
    error: string | null;
    hasNext: boolean;
    hasPrev: boolean;
    onNext: () => void;
    onPrev: () => void;
    onRetry: () => void;
};

function statusLabel(status: AdminWhatsappSendLogRow['status']) {
    return status === 'ok' ? 'Enviado' : 'Falhou';
}

export function WhatsappSendLogSection({
    items,
    total,
    limit,
    offset,
    isLoading,
    error,
    hasNext,
    hasPrev,
    onNext,
    onPrev,
    onRetry,
}: Props) {
    const rangeStart = total === 0 ? 0 : offset + 1;
    const rangeEnd = Math.min(offset + limit, total);

    return (
        <section aria-label="Logs de envio" className="mt-s5">
            <h2 className="mb-s3 text-base font-semibold text-default">Logs de envio</h2>

            {isLoading && (
                <div className="flex items-center justify-center py-12">
                    <Spinner color="primary" size="lg" />
                </div>
            )}

            {!isLoading && error && (
                <Alert variant="destructive">
                    <AlertTitle>Erro ao carregar logs</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <p>{error}</p>
                        <Button variant="outline" size="sm" onClick={onRetry}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && items.length === 0 && (
                <p className="py-8 text-center text-sm text-muted">Nenhum envio registrado</p>
            )}

            {!isLoading && !error && items.length > 0 && (
                <>
                    <ul className="m-0 flex list-none flex-col gap-3 p-0 md:hidden">
                        {items.map((row) => (
                            <li
                                key={row.id}
                                className="rounded-xl border border-subtle bg-card p-4"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <time className="text-xs text-muted" dateTime={row.sentAt}>
                                        {formatDate(row.sentAt)}
                                    </time>
                                    <span
                                        className={
                                            row.status === 'ok'
                                                ? 'text-sm text-[var(--color-success-600,#16a34a)]'
                                                : 'text-sm text-destructive'
                                        }
                                    >
                                        {statusLabel(row.status)}
                                    </span>
                                </div>
                                <code className="mt-2 block truncate font-mono text-xs text-secondary">
                                    {row.templateKey}
                                </code>
                                <p className="mt-1 font-mono text-sm text-muted">{row.toPhone}</p>
                                {row.errorDetail && (
                                    <p className="mt-2 line-clamp-2 text-xs text-muted">
                                        {whatsappSendErrorText(row.errorDetail)}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="hidden overflow-x-auto rounded-xl border border-subtle md:block">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-subtle bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)]">
                                    <th className="px-4 py-3 font-medium text-secondary">Data</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Template</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Número</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Status</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Erro</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((row) => (
                                    <tr key={row.id} className="border-b border-subtle last:border-b-0">
                                        <td className="px-4 py-3 whitespace-nowrap text-muted">
                                            {formatDate(row.sentAt)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <code className="font-mono text-xs text-secondary">
                                                {row.templateKey}
                                            </code>
                                        </td>
                                        <td className="px-4 py-3 font-mono whitespace-nowrap text-muted">
                                            {row.toPhone}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={
                                                    row.status === 'ok'
                                                        ? 'text-[var(--color-success-600,#16a34a)]'
                                                        : 'text-destructive'
                                                }
                                            >
                                                {statusLabel(row.status)}
                                            </span>
                                        </td>
                                        <td
                                            className="max-w-[280px] truncate px-4 py-3 text-muted"
                                            title={row.errorDetail ?? undefined}
                                        >
                                            {row.errorDetail ? whatsappSendErrorText(row.errorDetail) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-s3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-muted">
                            {rangeStart}–{rangeEnd} de {total}
                        </p>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 sm:flex-none"
                                onClick={onPrev}
                                disabled={!hasPrev}
                            >
                                Anterior
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 sm:flex-none"
                                onClick={onNext}
                                disabled={!hasNext}
                            >
                                Próxima
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}
